

export const dynamic = "force-dynamic";

import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import BusinessInfoSection from "./BusinessInfoSection";
import SettingsSidebar from "./SettingsSidebar";
import GeneralSettingsSection from "./GeneralSettingsSection";
import EmailSettingsSection from "./EmailSettingsSection";
import PWASettingsSection from "./PWASettingsSection";
import BackupExportSection from "./BackupExportSection";
import DataManagementSection from "./DataManagementSection";
import DocumentSettingsSection from "./DocumentSettingsSection";
import RolePermissionsSection from "./RolePermissionsSection";
import { useToast } from "@/context/ToastContext";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/hooks/useAuth";
import { dataManagementApi, imageApi, MAIN_URL } from "@/lib/api";
import {
  getSettings,
  saveSettings,
  syncSettingsToBackend,
  loadSettingsFromBackend,
  saveSMTPSettingsToBackend,
  loadSMTPSettingsFromBackend,
  testSMTPConnection,
  saveSMTPSettings,
  type SMTPSettings
} from "@/lib/settings";
import { getImageUrl } from "@/lib/utils";
import {
  type GeneralSettings,
  type BusinessInfo,
  type PWASettings
} from "@/context/SettingsContext";
import type { TabId } from "./SettingsSidebar";
import { DataSummary } from "./types";

export default function SettingsPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const { updateSettings } = useSettings();
  const isStaff = user?.role === 'staff';
  const isSuperAdmin = user?.role === 'super_admin';
  // ?tab=data etc. opens a section directly (old /data-management links land here)
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const tab = searchParams.get("tab");
    const known: TabId[] = ["business", "general", "email", "backup", "data", "pwa", "documents", "roles"];
    return tab && (known as string[]).includes(tab) ? (tab as TabId) : "business";
  });

  // Data Management State
  const [dataSummary, setDataSummary] = useState<DataSummary | null>(null);
  const [loadingDataSummary, setLoadingDataSummary] = useState(false);
  const [resettingData, setResettingData] = useState(false);
  const [generatingDummyData, setGeneratingDummyData] = useState(false);
  const [importingData, setImportingData] = useState(false);

  // Import/Export State
  const [exportingData, setExportingData] = useState(false);

  // General Settings State
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({
    language: "en",
    timezone: "Asia/Kolkata",
    dateFormat: "DD/MM/YYYY",
    currency: "INR",
    taxRate: "18",
    quotationValidityDays: "7",
    testingMode: false,
    quickLoginEmail: "",
    quickLoginPassword: "",
    paymentEditPassword: "",
  });

  // Business Info State
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    businessName: "",
    tagline: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    gstNumber: "",
    licenseNumber: "",
  });

  // Logo and Favicon State
  const [logoUrl, setLogoUrl] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");

  // SMTP Settings State
  const [smtpSettings, setSmtpSettings] = useState<SMTPSettings>({
    host: "",
    port: 587,
    secure: false,
    user: "",
    pass: "",
    fromName: "",
    fromEmail: "",
    enabled: false,
  });

  // PWA Settings State
  const [pwaSettings, setPwaSettings] = useState<PWASettings>({
    appName: "",
    shortName: "",
    description: "",
    icon192: "",
    icon512: "",
    installPromptEnabled: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load settings from backend on mount
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        // Try to load from backend first
        const backendSettings = await loadSettingsFromBackend();

        if (backendSettings) {
          setGeneralSettings(backendSettings.general);
          setBusinessInfo(backendSettings.business);
          setSmtpSettings(backendSettings.smtp);
          setPwaSettings(backendSettings.pwa);
          setLogoUrl(backendSettings.logoUrl || "");
          setFaviconUrl(backendSettings.faviconUrl || "");
        } else {
          // Fall back to localStorage if backend fails
          const settings = getSettings();
          setGeneralSettings(settings.general);
          setBusinessInfo(settings.business);
          setSmtpSettings(settings.smtp);
          setPwaSettings(settings.pwa);
          setLogoUrl(settings.logoUrl || "");
          setFaviconUrl(settings.faviconUrl || "");
        }
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Load data summary when data tab is active
  useEffect(() => {
    if (activeTab === "data") {
      loadDataSummary();
    }
  }, [activeTab]);

  const loadDataSummary = async () => {
    setLoadingDataSummary(true);
    try {
      const response = await dataManagementApi.getSummary();
      if (response.success) {
        setDataSummary(response.data);
      }
    } catch (error) {
      console.error("Failed to load data summary:", error);
      showToast("Failed to load data summary", "error");
    } finally {
      setLoadingDataSummary(false);
    }
  };

  const handleResetData = async (type: 'all' | 'operational', confirmText: string): Promise<boolean> => {
    setResettingData(true);
    try {
      const response = await dataManagementApi.resetData(type, confirmText);
      if (response.success) {
        // The message names the backup file saved before deleting
        showToast(response.message || "Data has been reset", "success");
        await loadDataSummary();
        return true;
      }
      showToast(response.message || "Failed to reset data", "error");
      return false;
    } catch (error: any) {
      showToast(error.message || "Failed to reset data", "error");
      return false;
    } finally {
      setResettingData(false);
    }
  };

  const handleGenerateDummyData = async () => {
    setGeneratingDummyData(true);
    try {
      const response = await dataManagementApi.generateDummyData();
      if (response.success) {
        showToast("Dummy data generated successfully", "success");
        await loadDataSummary();
      } else {
        showToast(response.message || "Failed to generate dummy data", "error");
      }
    } catch (error: any) {
      showToast(error.message || "Failed to generate dummy data", "error");
    } finally {
      setGeneratingDummyData(false);
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const response = await dataManagementApi.exportData();
      if (response.success) {
        // Create and download the JSON file
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `shalimar-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast("Data exported successfully", "success");
      } else {
        showToast(response.message || "Failed to export data", "error");
      }
    } catch (error: any) {
      showToast(error.message || "Failed to export data", "error");
    } finally {
      setExportingData(false);
    }
  };

  const handleImportData = async (file: File, strategy: 'replace' | 'merge' | 'skip') => {
    setImportingData(true);
    try {
      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      const response = await dataManagementApi.importData(jsonData, strategy);
      if (response.success) {
        showToast(`Data imported successfully. ${Object.keys(response.data.imported).length} tables updated.`, "success");
        await loadDataSummary();
      } else {
        showToast(response.message || "Failed to import data", "error");
      }
    } catch (error: any) {
      showToast(error.message || "Failed to import data. Make sure the file is valid JSON.", "error");
    } finally {
      setImportingData(false);
    }
  };

  const handleSaveSMTP = async () => {
    setSaving(true);
    try {
      const success = await saveSMTPSettingsToBackend(smtpSettings);
      if (success) {
        // The password now lives only on the server
        const saved = { ...smtpSettings, pass: "", hasPassword: Boolean(smtpSettings.pass || smtpSettings.hasPassword) };
        setSmtpSettings(saved);
        saveSMTPSettings(saved);
        showToast("Email settings saved", "success");
      } else {
        showToast("Email settings were not saved. Only an admin can change them.", "error");
      }
    } catch (error) {
      showToast("Email settings were not saved. Check your connection and try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMTP = async (testEmail: string): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await testSMTPConnection(testEmail);

      if (result.success) {
        showToast(result.message, "success");
      } else {
        showToast(result.message, "error");
      }

      return result;
    } catch (error: any) {
      showToast(error.message || "Connection failed", "error");
      return { success: false, message: error.message || "Connection failed" };
    }
  };

  // Function to generate all needed logos from main logo
  const generateLogosFromMainLogo = async () => {
    if (!logoUrl) {
      showToast("Please upload a main logo first", "error");
      return;
    }

    const imageUrl = getImageUrl(logoUrl);
    if (!imageUrl) {
      showToast("Invalid logo URL. Please re-upload the logo.", "error");
      return;
    }

    try {
      setSaving(true);

      let blob: Blob;

      // If it's a blob URL (just uploaded), fetch it directly
      if (imageUrl.startsWith('blob:')) {
        const response = await fetch(imageUrl);
        blob = await response.blob();
      } else {
        // For remote URLs, fetch through API to avoid CORS
        // Extract the path from the URL
        const urlPath = imageUrl.replace(MAIN_URL, '');
        const response = await fetch(`${MAIN_URL}/api/image-proxy?path=${encodeURIComponent(urlPath)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }
        blob = await response.blob();
      }

      // Create image from blob
      const img = new Image();
      const blobUrl = URL.createObjectURL(blob);

      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          URL.revokeObjectURL(blobUrl);
          resolve();
        };
        img.onerror = () => {
          URL.revokeObjectURL(blobUrl);
          reject(new Error('Failed to load image'));
        };
        img.src = blobUrl;
      });

      // Create different sizes needed for PWA
      const sizes = [
        { name: '192', width: 192, height: 192 },
        { name: '512', width: 512, height: 512 },
      ];

      for (const size of sizes) {
        // Create canvas for resizing
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Canvas context not available');
        }
        canvas.width = size.width;
        canvas.height = size.height;

        // Draw and resize image
        ctx.drawImage(img, 0, 0, size.width, size.height);

        // Convert to blob
        const resizedBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create resized blob'));
          }, 'image/png', 0.9);
        });

        // Create file for upload
        const file = new File([resizedBlob], `logo-${size.name}.png`, { type: 'image/png' });

        // Upload the resized logo
        const uploadResult = await imageApi.upload(file, 'pwa', `pwa.icon${size.name}`);

        // Update PWA settings - API returns { success: true, data: { url: '...' } }
        const uploadedUrl = uploadResult.data?.url || uploadResult.url;
        if (uploadedUrl) {
          setPwaSettings(prev => ({
            ...prev,
            [`icon${size.name}`]: uploadedUrl
          }));
        }
      }

      showToast("All logos generated successfully!", "success");
    } catch (error) {
      console.error('Error generating logos:', error);
      showToast(error instanceof Error ? error.message : "Failed to generate logos", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (section: string, data: unknown, extraData?: { logoUrl?: string; faviconUrl?: string }) => {
    // Staff cannot modify settings
    if (isStaff) {
      showToast("Staff users cannot modify settings", "error");
      return;
    }

    setSaving(true);
    try {
      // Build save data with extra fields for PWA section
      const saveData: Record<string, unknown> = { [section.toLowerCase()]: data };
      if (extraData?.logoUrl !== undefined) {
        saveData.logoUrl = extraData.logoUrl;
      }
      if (extraData?.faviconUrl !== undefined) {
        saveData.faviconUrl = extraData.faviconUrl;
      }

      // Save on the server; nothing is kept if the server refuses
      const saved = await syncSettingsToBackend(saveData);
      if (!saved) {
        showToast(`${section} settings were not saved. Only an admin can change them.`, "error");
        return;
      }

      // Cache for instant display next time (passwords are stripped)
      saveSettings(saveData);

      // Update SettingsContext for immediate reflection across app
      if (section.toLowerCase() === 'business') {
        updateSettings({ business: data as BusinessInfo });
      } else if (section.toLowerCase() === 'pwa') {
        updateSettings({
          pwa: data as PWASettings,
          logoUrl: extraData?.logoUrl || logoUrl,
          faviconUrl: extraData?.faviconUrl || faviconUrl
        });
      } else if (section.toLowerCase() === 'general') {
        updateSettings({ general: data as GeneralSettings });
      }

      showToast(`${section} settings saved successfully!`, "success");
    } catch (error: any) {
      console.error("Error saving settings:", error);
      showToast(`${section} settings were not saved: ${error.message || "server not reachable"}`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Sidebar Tabs */}
        <SettingsSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          loading={loading}
          isStaff={isStaff}
          isSuperAdmin={isSuperAdmin}
        />

        {/* Content Area */}
        <div className="flex-1">
          {/* Staff Read-Only Notice */}
          {isStaff && (
            <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-2 text-amber-800">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-sm">Read-Only Mode</span>
              </div>
              <p className="text-xs text-amber-700 mt-1">
                Staff users can view settings but cannot modify them. Contact an administrator for changes.
              </p>
            </div>
          )}

          {/* General Settings */}
          {activeTab === "general" && (
            <GeneralSettingsSection
              data={generalSettings}
              onChange={setGeneralSettings}
              onSave={(data) => handleSave("General", data)}
              saving={saving}
              readOnly={isStaff}
            />
          )}

          {/* Business Info */}
          {activeTab === "business" && (
            <BusinessInfoSection
              data={businessInfo}
              onChange={setBusinessInfo}
              onSave={(data) => handleSave("Business", data)}
              saving={saving}
              readOnly={isStaff}
            />
          )}

          {/* Email / SMTP Settings */}
          {activeTab === "email" && (
            <EmailSettingsSection
              data={smtpSettings}
              onChange={setSmtpSettings}
              onSave={handleSaveSMTP}
              onTest={handleTestSMTP}
              saving={saving}
              readOnly={isStaff}
            />
          )}

          {/* PWA / App Settings */}
          {activeTab === "pwa" && (
            <PWASettingsSection
              data={pwaSettings}
              logoUrl={logoUrl}
              faviconUrl={faviconUrl}
              onChange={setPwaSettings}
              onSave={(data) => handleSave("PWA", data, { logoUrl, faviconUrl })}
              onGenerateLogos={generateLogosFromMainLogo}
              onLogoChange={setLogoUrl}
              onFaviconChange={setFaviconUrl}
              saving={saving}
              readOnly={isStaff}
            />
          )}

          {/* Document Settings (Invoice & Quotation) */}
          {activeTab === "documents" && (
            <DocumentSettingsSection readOnly={isStaff} />
          )}

          {/* Roles & Page Access */}
          {activeTab === "roles" && !isStaff && (
            <RolePermissionsSection />
          )}

          {/* Backup & Export */}
          {activeTab === "backup" && (
            <BackupExportSection
              onExport={handleExportData}
              exporting={exportingData}
              readOnly={isStaff}
            />
          )}

          {/* Data Management — super_admin only */}
          {activeTab === "data" && isSuperAdmin && (
            <DataManagementSection
              dataSummary={dataSummary}
              loadingDataSummary={loadingDataSummary}
              onResetData={handleResetData}
              onGenerateDummyData={handleGenerateDummyData}
              onImportData={handleImportData}
              resettingData={resettingData}
              generatingDummyData={generatingDummyData}
              importingData={importingData}
              readOnly={isStaff}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
